/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Ladda from 'ladda';
import Backgrid from 'backgrid';
import moment from 'moment';
import Backbone from 'backbone';

import Session from '@/Session';
import { formatDisplayDate, formatDisplayDateTime } from '@/util/MomentUtils';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';

import DropzoneTextInputView from '../DropzoneTextInputView';
import PageableCollection from '../../backbone/PageableCollection';

import CommunicationEntryCollectionView from './CommunicationEntryCollectionView';
import tmplCommunicationView from './CommunicationView.hbs';
import './CommunicationsView.css';

const CommunicationView = View.extend({
  template: tmplCommunicationView,

  className: 'communications-view',

  ui: {
    addCommunication: '.add-communication',
    communicationEntryCollectionContainer: '.communication-entry-collection-container',
    paginator: '.paginator-container',
    dropzoneTextInput: '.dropzone-text-input'
  },

  regions: {
    communicationEntryCollectionContainer: '@ui.communicationEntryCollectionContainer',
    paginator: '@ui.paginator',
    dropzoneTextInput: '@ui.dropzoneTextInput'
  },

  events: {
    'click @ui.addCommunication': 'onClickAddCommunication'
  },

  childViewEvents: {
    'upload:documentUpload': 'onChildDropzoneDocumentUpload',
    'error:documentUploadError': 'onChildDropzoneDocumentUploadError'
  },

  initialize(options) {
    this.submitCommunicationService = options.submitCommunicationService;
  },

  onRender() {
    this.orderID = this.model.get('orderId');
    this.assessmentID = this.model.get('assessmentId');
    const communications = this.model.get('communications') || [];

    this.collection = new PageableCollection(communications, {
      pageSize: 10
    });

    this.collection.fullCollection.comparator = function (memo1, memo2) {
      const date1 = memo1.get('createdOn');
      const date2 = memo2.get('createdOn');
      return date1 > date2;
    };

    this.communicationsEntryCollection = new CommunicationEntryCollectionView({
      collection: this.collection
    });
    this.showChildView('communicationEntryCollectionContainer', this.communicationsEntryCollection);

    this.showChildView(
      'paginator',
      new Backgrid.Extension.Paginator({
        collection: this.collection
      })
    );

    if (this.model.get('editable')) {
      this.dropzoneTextInput = new DropzoneTextInputView({
        model: new Backbone.Model({
          metadata: this.model.get('metadata'),
          tmplMethodXML: this.model.get('submitCommunicationAttachmentService'),
          pathToFileId: this.model.get('pathToFileId')
        })
      });
      this.showChildView('dropzoneTextInput', this.dropzoneTextInput);
    }
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  showSaveSuccessMessage() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Submit Success',
      message: 'Communication saved successfully.'
    });
  },

  showSaveErrorMessage(e) {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage: 'An error occurred while saving communication. Please try again or contact an administrator.'
    });
  },

  async submitMemo() {
    const dropzoneData = this.dropzoneTextInput.getFormData();
    let formData;

    if (this.model.get('orderId')) {
      formData = {
        memo: dropzoneData.dropzoneText,
        orderId: this.model.get('orderId')
      };
    } else if (this.model.get('assessmentId')) {
      formData = {
        memo: dropzoneData.dropzoneText,
        assessmentId: this.model.get('assessmentId')
      };
    } else if (this.model.get('customerId')) {
      formData = {
        memo: dropzoneData.dropzoneText,
        customerId: this.model.get('customerId')
      };
    }

    await this.dropzoneTextInput.validate();
    this.toggleForm(false);

    try {
      await this.submitCommunicationService(formData);
      this.showSaveSuccessMessage();

      formData = { ...formData, ...this.getFormData() };

      this.collection.add(formData);
      this.collection.fullCollection.sort();
      this.dropzoneTextInput.resetDropzone();
    } catch (err) {
      this.showSaveErrorMessage();
    } finally {
      this.toggleForm(true);
    }
  },

  onClickAddCommunication(e) {
    e.preventDefault();

    let spinner;
    if (e) {
      spinner = Ladda.create(e.currentTarget);
      spinner.start();
    }

    try {
      this.submitMemo();
    } catch (err) {
      //validation?
    } finally {
      this.toggleForm(true);
      spinner.stop();
    }
  },

  getFormData() {
    const datetime = formatDisplayDateTime(moment());
    return {
      firstName: Session.user.firstName,
      lastName: Session.user.lastName,
      createdDate: formatDisplayDate(moment()),
      createdOn: datetime,
      createdTime: datetime.substr(datetime.indexOf(' ') + 1)
    };
  },

  onChildDropzoneDocumentUpload(formData) {
    formData = { ...formData, ...this.getFormData() };

    this.collection.add(formData);
    this.collection.fullCollection.sort();

    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Submit Success',
      message: 'Attachment Added.'
    });

    this.dropzoneTextInput.resetDropzone();
  },

  onChildDropzoneDocumentUploadError(e) {
    if (e?.responseText.includes('already exists')) {
      showErrorMessage({
        errorCode: 'documentExists',
        errorTitle: 'Duplicate File Name',
        errorMessage:
          'All uploaded documents must have a unique file name. Please ensure that a document with the same name has not been uploaded in other areas of the form.'
      });
    } else {
      showErrorMessage({
        errorCode: 'uploadError',
        errorTitle: 'Upload Error',
        errorMessage: 'An error occurred while uploading this file. Please try again or contact an administrator.'
      });
    }
  }
});

export default CommunicationView;
